import { getPublicEnv, type PublicEnvKey } from "@janeway/app-sdk/public-env";

export interface JanewayApp {
  /** Stabiler Identifier — matcht die janeway-status Service-ID (fuer Live-Status). */
  id: string;
  name: string;
  url: string;
  logo: string;
  /**
   * Optionaler lucide-Glyph-Name (PascalCase, z.B. "Building2"). Kommt aus der
   * live `infra_apps`-Registry und erlaubt neuen Apps ein Icon OHNE Code-Aenderung
   * (AppIcon loest ihn dynamisch auf). Bei den statischen Apps ungesetzt — dort
   * greift der bestehende APP_GLYPHS-Lookup ueber die id.
   */
  glyph?: string;
  provides?: import("./app-manifest").Capability[];
  consumes?: import("./app-manifest").Capability[];
}

/**
 * Zeile der live `infra_apps`-Registry (Supabase). Spiegelt das Schema aus
 * Migration 219 — Source of Truth fuer AppSwitcher + Hub, wenn erreichbar.
 */
export interface InfraAppRow {
  id: string;
  name: string;
  sub: string;
  glyph: string | null;
  category: string;
  sort_order: number;
  enabled: boolean;
  provides?: string[];
  consumes?: string[];
}

interface AppDef {
  id: string;
  name: string;
  /** Subdomain (z.B. "chat") — fuer Hostname-Ableitung, wenn keine env-URL gesetzt. */
  sub: string;
  /**
   * Optionaler Override. Es gibt NICHT fuer jede App eine `NEXT_PUBLIC_*_URL`:
   * drive, docs, sheets, slides, meet und mail haben keine (`PUBLIC_ENV_KEYS` im
   * app-sdk ist die Liste). Ohne Key faellt die Aufloesung auf
   * `sub + baseDomain` — genau der Weg, den `mapInfraAppsToJanewayApps` fuer
   * Registry-Apps ohne Override schon geht.
   */
  envKey?: PublicEnvKey;
  logo: string;
}

/**
 * Statischer Fallback fuer `useJanewayApps`, wenn die `infra_apps`-Registry nicht
 * antwortet ("GARANTIE: NIE ein Throw im Render-Pfad").
 *
 * **Er muss die Prod-Registry abbilden, nicht eine Teilmenge.** Bis 2026-09-08
 * standen hier 9 von 16 Prod-Apps; bei jedem Aussetzer der Abfrage sah der Kunde
 * still 9 statt 16 Kacheln — ohne Fehler, ohne Hinweis — und las das als „meine
 * Apps sind weg" (ARCH-16). Es fehlten mail-client, drive, docs, sheets, slides,
 * meet und messenger.
 *
 * Die Gegenprobe steht in `app-registry.prod.json` (Abzug der Prod-Registry mit
 * Stand-Datum und dem Befehl, der ihn erzeugt); `src/__tests__/apps.test.ts`
 * haelt beide Listen gegeneinander, statt die Ids ein zweites Mal aufzuschreiben.
 * Wer eine App hinzufuegt, zieht den Abzug nach und traegt sie hier ein — der
 * Test nennt dann beide Seiten.
 *
 * Kein Glyph-Feld noetig: `APP_GLYPHS` loest alle diese Ids auf.
 */
const APP_DEFS: AppDef[] = [
  { id: "janeway-ai-chat",     name: "AI Chat",     sub: "chat",        envKey: "NEXT_PUBLIC_AI_URL",        logo: "/app-logos/ai.png" },
  { id: "janeway-automations", name: "Automations", sub: "automations", envKey: "NEXT_PUBLIC_AGENTS_URL",    logo: "/app-logos/agents.png" },
  { id: "janeway-notes",       name: "Notes",       sub: "notes",       envKey: "NEXT_PUBLIC_NOTES_URL",     logo: "/app-logos/notes.png" },
  { id: "janeway-contacts",    name: "Contacts",    sub: "contacts",    envKey: "NEXT_PUBLIC_CONTACTS_URL",  logo: "/app-logos/contacts.png" },
  { id: "janeway-calendar",    name: "Calendar",    sub: "calendar",    envKey: "NEXT_PUBLIC_CALENDAR_URL",  logo: "/app-logos/calendar.png" },
  { id: "janeway-tasks",       name: "Tasks",       sub: "tasks",       envKey: "NEXT_PUBLIC_TASKS_URL",     logo: "/app-logos/tasks.png" },
  { id: "janeway-immo",        name: "Immobilien",  sub: "immo",        envKey: "NEXT_PUBLIC_IMMO_URL",      logo: "/app-logos/immo.png" },
  { id: "janeway-mail-client", name: "Mail",        sub: "mail",                                             logo: "/app-logos/mail.png" },
  { id: "janeway-drive",       name: "Drive",       sub: "drive",                                            logo: "/app-logos/drive.png" },
  { id: "janeway-docs",        name: "Docs",        sub: "docs",                                             logo: "/app-logos/docs.png" },
  { id: "janeway-sheets",      name: "Sheets",      sub: "sheets",                                           logo: "/app-logos/sheets.png" },
  { id: "janeway-slides",      name: "Slides",      sub: "slides",                                           logo: "/app-logos/slides.png" },
  { id: "janeway-meet",        name: "Meet",        sub: "meet",                                             logo: "/app-logos/meet.png" },
  { id: "janeway-messenger",   name: "Messenger",   sub: "messenger",   envKey: "NEXT_PUBLIC_MESSENGER_URL", logo: "/app-logos/messenger.png" },
  { id: "janeway-affiliate",   name: "Affiliate",   sub: "affiliate",   envKey: "NEXT_PUBLIC_AFFILIATE_URL", logo: "/app-logos/affiliate.png" },
  { id: "janeway-api",         name: "API",         sub: "api",         envKey: "NEXT_PUBLIC_API_URL",       logo: "/app-logos/api.png" },
];

/**
 * Subdomain -> Public-Env-Key. Pro Install kann eine explizite `NEXT_PUBLIC_*_URL`
 * gesetzt sein (hat Vorrang vor der baseDomain-Ableitung). Deckt die bekannten
 * Subs ab; unbekannte (neue Apps aus der DB) haben keinen Override und fallen
 * sauber auf `sub.baseDomain` zurueck.
 */
const SUB_ENV_KEYS: Record<string, PublicEnvKey> = {
  chat: "NEXT_PUBLIC_AI_URL",
  automations: "NEXT_PUBLIC_AGENTS_URL",
  notes: "NEXT_PUBLIC_NOTES_URL",
  contacts: "NEXT_PUBLIC_CONTACTS_URL",
  calendar: "NEXT_PUBLIC_CALENDAR_URL",
  tasks: "NEXT_PUBLIC_TASKS_URL",
  immo: "NEXT_PUBLIC_IMMO_URL",
  affiliate: "NEXT_PUBLIC_AFFILIATE_URL",
  api: "NEXT_PUBLIC_API_URL",
  admin: "NEXT_PUBLIC_ADMIN_URL",
  status: "NEXT_PUBLIC_STATUS_URL",
  auth: "NEXT_PUBLIC_AUTH_URL",
  hub: "NEXT_PUBLIC_HUB_URL",
  messenger: "NEXT_PUBLIC_MESSENGER_URL",
};

/**
 * Loest eine Cross-App-URL auf. Reihenfolge:
 *   1. env (`NEXT_PUBLIC_*_URL`, pro Install gesetzt) — hat Vorrang
 *   2. aus `baseDomain` abgeleitet (`https://<sub>.<baseDomain>`) — labs zeigt auf labs
 *   3. prod-Fallback (`https://<sub>.janeway.io`)
 * `baseDomain` kommt vom Server (Host-Header) -> SSR und Client liefern identische
 * Hrefs (keine Hydration-Mismatch) und die richtigen Links stehen schon im SSR-HTML.
 */
export function janewayUrl(
  sub: string,
  envKey?: PublicEnvKey,
  baseDomain?: string | null,
): string {
  return (envKey && getPublicEnv(envKey)) || `https://${sub}.${baseDomain || "janeway.io"}`;
}

/**
 * Kanonische App-Registry — Source of Truth fuer AppSwitcher (TopNav) UND
 * HubLauncher. `baseDomain` optional: ohne -> env/prod (AppSwitcher-Default),
 * mit -> Hostname-Ableitung (Hub, server-seitig befuellt).
 */
export function getJanewayApps(baseDomain?: string | null): JanewayApp[] {
  return APP_DEFS.map((d) => ({ id: d.id, name: d.name, url: janewayUrl(d.sub, d.envKey, baseDomain), logo: d.logo }));
}


/**
 * Weitere Dienste (Affiliate, API, Admin, Status) — hinter „Mehr" ausklappbar
 * (AppSwitcher UND Hub-Grid), getrennt von den Enduser-Suite-Apps. Admin ist
 * bewusst fuer alle sichtbar: fehlt die Berechtigung, faengt die Ziel-App das ab
 * (Hinweis + Login). Logos im Stil der uebrigen app-logos.
 */
export function getSystemApps(baseDomain?: string | null): JanewayApp[] {
  return [
    // affiliate + api sind jetzt Default-Apps (APP_DEFS/'main', siehe Migration 230) —
    // hier bleiben nur admin + status hinter "Mehr".
    { id: "janeway-admin", name: "Admin", url: janewayUrl("admin", "NEXT_PUBLIC_ADMIN_URL", baseDomain), logo: "/app-logos/admin.png" },
    { id: "janeway-status", name: "Status", url: janewayUrl("status", "NEXT_PUBLIC_STATUS_URL", baseDomain), logo: "/app-logos/status.png" },
  ];
}

/**
 * Mappt die live `infra_apps`-Registry auf `JanewayApp[]`, getrennt nach
 * `main` (Suite-Apps) und `system` (weitere Dienste). URL wird wie bei den
 * statischen Apps aufgeloest (env-Override -> sub+baseDomain -> prod-Fallback);
 * der Glyph-Name wird durchgereicht, sodass neue Apps OHNE Code-Aenderung ein
 * Icon bekommen. Das `logo`-Feld bleibt aus Kompat-Gruenden gesetzt (Initialen-
 * Fallback im logo-Modus), getragen wird das Icon aber ueber den Glyph.
 *
 * Pur + deterministisch (keine I/O) — der Hook ruft das mit den DB-Zeilen auf.
 */
export function mapInfraAppsToJanewayApps(
  rows: InfraAppRow[],
  baseDomain?: string | null,
): { main: JanewayApp[]; system: JanewayApp[] } {
  const toApp = (r: InfraAppRow): JanewayApp => {
    const envKey = SUB_ENV_KEYS[r.sub];
    const envUrl = envKey ? getPublicEnv(envKey) : undefined;
    return {
      id: r.id,
      name: r.name,
      url: envUrl || `https://${r.sub}.${baseDomain || "janeway.io"}`,
      logo: `/app-logos/${r.sub}.png`,
      glyph: r.glyph ?? undefined,
      provides: (r.provides ?? []) as import("./app-manifest").Capability[],
      consumes: (r.consumes ?? []) as import("./app-manifest").Capability[],
    };
  };
  const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order);
  return {
    system: sorted.filter((r) => r.category === "system").map(toApp),
    // alles ausser "system" ist Suite-App (unbekannte category faellt auf main).
    main: sorted.filter((r) => r.category !== "system").map(toApp),
  };
}

/**
 * Leitet die Hub-URL aus dem aktuellen Hostname ab: `chat.janewaylabs.com` ->
 * `https://hub.janewaylabs.com`, `tools.janeway.io` -> `https://hub.janeway.io`.
 * Liefert `null` bei localhost / IP / Apex (<3 Labels) — dann greift env oder der
 * `"/"`-Fallback. Spiegelt `auth/lib/auth-helpers.deriveHubUrl`, lebt aber hier,
 * weil der AppSwitcher (gui-elements) keinen Zugriff auf die auth-App hat.
 *
 * WARUM: `NEXT_PUBLIC_HUB_URL` ist nicht in jeder Install gesetzt; ohne diese
 * Ableitung fiel die "Alle Apps"-Aktion auf `"/"` (= aktuelle App) zurueck.
 */
export function deriveHubUrlFromHost(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  const host = hostname.split(":")[0];
  if (/^[\d.]+$/.test(host)) return null; // IP
  const parts = host.split(".");
  if (parts.length < 3) return null; // localhost / Apex
  parts[0] = "hub";
  return `https://${parts.join(".")}`;
}

/**
 * Leitet die Basis-Domain aus dem Hostname ab: `immo.janewaylabs.com` ->
 * `janewaylabs.com`, `contacts.janeway.io` -> `janeway.io`. `null` bei
 * localhost / IP / Apex (<3 Labels). So zeigt der URL-Fallback fuer Registry-Apps
 * OHNE `NEXT_PUBLIC_*_URL`-Override (z.B. immo) auf die AKTUELLE Umgebung statt
 * hartkodiert auf `janeway.io`.
 */
export function deriveBaseDomain(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  const host = hostname.split(":")[0];
  if (/^[\d.]+$/.test(host)) return null; // IP
  const parts = host.split(".");
  if (parts.length < 3) return null; // localhost / Apex
  return parts.slice(1).join("."); // erstes Label (Subdomain) abschneiden
}

/**
 * Kanonische Hub-URL fuer "Alle Apps": env hat Vorrang, sonst aus dem aktuellen
 * Hostname abgeleitet, sonst `"/"`. `baseDomain` erlaubt server-seitige Aufloesung.
 */
export function janewayHubUrl(baseDomain?: string | null): string {
  const env = getPublicEnv("NEXT_PUBLIC_HUB_URL");
  if (env) return env;
  if (baseDomain) return `https://hub.${baseDomain}`;
  if (typeof window !== "undefined") {
    const derived = deriveHubUrlFromHost(window.location.hostname);
    if (derived) return derived;
  }
  return "/";
}

/**
 * Geordnete Favoriten-Aufteilung — geteilt von AppSwitcher (TopNav) UND Hub-Grid,
 * damit beide dieselbe „direkt sichtbar vs. hinter ‚Mehr'"-Logik nutzen:
 *   - `direct`: die Favoriten in `favoriteIds`-Reihenfolge (NICHT Katalog-Reihen-
 *     folge), nur existierende & nicht-ausgeblendete Apps. Unbekannte ids (App aus
 *     der Registry verschwunden) werden uebersprungen.
 *   - `more`: der Rest (nicht-Favorit, nicht-ausgeblendet) in `allApps`-Reihenfolge.
 *   - ausgeblendete (`hiddenIds`) tauchen in KEINER der beiden Listen auf.
 *
 * Pur + deterministisch (keine I/O). `hiddenIds` hat Vorrang vor `favoriteIds`
 * (eine App, die zugleich Favorit und ausgeblendet ist, bleibt ausgeblendet).
 *
 * `autoDirectIds` = ids, die auch OHNE Eintrag in `favoriteIds` nach `direct`
 * gehoeren. Hintergrund: `favoriteIds`/`hiddenIds` speichern nicht, welche Apps
 * der Nutzer je gesehen hat — „neu registriert" und „bewusst entsternt" sehen im
 * Datenmodell identisch aus (beides weder Favorit noch ausgeblendet). Ohne diesen
 * Parameter fiel jede nach dem letzten Speichern hinzugekommene App nach `more`
 * und verschwand hinter dem „Mehr"-Toggle des AppSwitchers. Der Aufrufer loest die
 * Mehrdeutigkeit ueber `user_preferences.hub_known_ids` auf und reicht
 * `mainIds \ knownIds` durch. Ausgeblendet schlaegt weiterhin alles.
 */
export function splitByFavorites(
  allApps: JanewayApp[],
  favoriteIds: string[],
  hiddenIds: string[],
  autoDirectIds: string[] = [],
): { direct: JanewayApp[]; more: JanewayApp[] } {
  const hiddenSet = new Set(hiddenIds);
  const byId = new Map(allApps.map((a) => [a.id, a]));

  const direct: JanewayApp[] = [];
  const seen = new Set<string>();
  for (const id of favoriteIds) {
    if (seen.has(id) || hiddenSet.has(id)) continue; // Duplikat / ausgeblendet
    const app = byId.get(id);
    if (!app) continue; // unbekannte id (App nicht mehr in der Registry)
    seen.add(id);
    direct.push(app);
  }

  // Neu hinzugekommene Apps ans ENDE von direct — die Nutzer-Sortierung der
  // echten Favoriten bleibt unberuehrt. Iteration ueber `allApps` (statt ueber
  // autoDirectIds), damit die Reihenfolge der Registry folgt und unbekannte ids
  // von selbst herausfallen.
  const autoSet = new Set(autoDirectIds);
  for (const app of allApps) {
    if (!autoSet.has(app.id) || seen.has(app.id) || hiddenSet.has(app.id)) continue;
    seen.add(app.id);
    direct.push(app);
  }

  const more = allApps.filter((a) => !seen.has(a.id) && !hiddenSet.has(a.id));
  return { direct, more };
}

/**
 * Verschiebt eine Favoriten-id im geordneten `favoriteIds`-Array um `delta`
 * Positionen (−1 = vor, +1 = zurueck) — fuer die Reorder-UI im Hub-Edit-Modus.
 * Liefert IMMER ein neues Array (kein In-place-Mutate). Out-of-bounds (am Anfang
 * weiter nach vorne / am Ende weiter nach hinten) ist ein No-Op und gibt die
 * Liste unveraendert zurueck; unbekannte id ebenso. Pur + deterministisch.
 */
export function moveFavorite(favoriteIds: string[], id: string, delta: number): string[] {
  const from = favoriteIds.indexOf(id);
  if (from === -1) return favoriteIds;
  const to = from + delta;
  if (to < 0 || to >= favoriteIds.length) return favoriteIds;
  const next = [...favoriteIds];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** Case-insensitive Filter nach App-Name (fuer den Type-to-filter-Launcher). */
export function filterApps(apps: JanewayApp[], query: string): JanewayApp[] {
  const q = query.trim().toLowerCase();
  if (!q) return apps;
  return apps.filter((a) => a.name.toLowerCase().includes(q));
}
